// src/components/common/Sidebar/Sidebar.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, IconButton, Tooltip, useMediaQuery, useTheme, Backdrop, Fab,
} from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import { AdminPanelSettings } from '@mui/icons-material';
import UserAvatar from '../UserAvatar/UserAvatar';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';

const SIDEBAR_WIDTH = 260;
const COLLAPSED_WIDTH = 64;

export default function Sidebar({ onStateChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [isOpen, setIsOpen] = useState(!isMobile);

  useEffect(() => {
    const newState = !isMobile;
    setIsOpen(newState);
    onStateChange?.(newState);
    window.dispatchEvent(new CustomEvent('sidebarStateChange', { detail: { isOpen: newState } }));
  }, [isMobile, onStateChange]);

  const menuItems = [
    { text: 'Video Dubbing', icon: <RecordVoiceOverIcon sx={{ fontSize: 20 }} />, path: '/dubbing' },
    { text: 'Audio Translation', icon: <MusicNoteIcon sx={{ fontSize: 20 }} />, path: '/audio-translation' },
    ...(isAdmin?.() ? [{ text: 'Admin Dashboard', icon: <AdminPanelSettings sx={{ fontSize: 20 }} />, path: '/admin/dashboard' }] : []),
  ];

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);
    onStateChange?.(next);
    window.dispatchEvent(new CustomEvent('sidebarStateChange', { detail: { isOpen: next } }));
  };

  const handleNav = (path) => {
    navigate(path);
    if (isMobile) toggle();
  };

  const handleLogout = () => {
    try {
      logout();
      showSuccess('Logged out successfully');
      navigate('/');
    } catch {
      showError('Failed to logout. Please try again.');
    }
  };

  return (
    <>
      {/* Mobile FAB */}
      {isMobile && !isOpen && (
        <Fab
          onClick={toggle}
          size="small"
          sx={{
            position: 'fixed', top: 16, left: 16, zIndex: 1300,
            backgroundColor: '#111111',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
            width: 44, height: 44, borderRadius: '10px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            '&:hover': { backgroundColor: '#1A1A1A' },
          }}
        >
          <MenuIcon sx={{ fontSize: 20 }} />
        </Fab>
      )}

      {/* Mobile Backdrop */}
      {isMobile && (
        <Backdrop open={isOpen} onClick={toggle} sx={{ zIndex: 1199, backgroundColor: 'rgba(0,0,0,0.7)' }} />
      )}

      {/* Sidebar */}
      <Box
        sx={{
          width: isMobile ? SIDEBAR_WIDTH : (isOpen ? SIDEBAR_WIDTH : COLLAPSED_WIDTH),
          height: '100vh',
          backgroundColor: '#080808',
          borderRight: '1px solid rgba(255,255,255,0.05)',
          display: 'flex',
          flexDirection: 'column',
          position: 'fixed',
          left: isMobile ? (isOpen ? 0 : -SIDEBAR_WIDTH) : 0,
          top: 0,
          zIndex: 1200,
          transition: isMobile ? 'left 0.28s ease' : 'width 0.28s ease',
          overflow: 'visible',
          ...(isMobile && { boxShadow: '0 0 40px rgba(0,0,0,0.6)' }),
        }}
      >
        {/* Desktop expand/collapse toggle */}
        {!isMobile && (
          <IconButton
            onClick={toggle}
            sx={{
              position: 'absolute', right: -16, top: '50%', transform: 'translateY(-50%)',
              width: 32, height: 32, borderRadius: '50%',
              backgroundColor: '#111111',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.4)',
              zIndex: 1300,
              '&:hover': { backgroundColor: '#1A1A1A', color: '#fff', borderColor: 'rgba(255,255,255,0.2)' },
            }}
          >
            {isOpen ? <ChevronLeftIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        )}

        {/* Mobile close */}
        {isMobile && isOpen && (
          <IconButton
            onClick={toggle}
            sx={{
              position: 'absolute', right: 12, top: 18,
              color: 'rgba(255,255,255,0.4)',
              '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.05)' },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        {/* Logo */}
        <Box
          onClick={() => { navigate('/'); if (isMobile) toggle(); }}
          sx={{
            display: 'flex', alignItems: 'center',
            px: (isOpen || isMobile) ? 2.5 : 0,
            py: 2.5,
            borderBottom: '1px solid rgba(255,255,255,0.05)',
            cursor: 'pointer', minHeight: 72,
            justifyContent: (isOpen || isMobile) ? 'flex-start' : 'center',
          }}
        >
          {(isOpen || isMobile) ? (
            <Typography sx={{
              fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '18px',
              letterSpacing: '-0.04em', whiteSpace: 'nowrap',
            }}>
              <span style={{ color: '#FFFFFF' }}>Dub</span>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>Labs</span>
            </Typography>
          ) : (
            <Typography sx={{
              fontFamily: 'Inter, sans-serif', fontWeight: 800, fontSize: '16px',
              color: '#fff', letterSpacing: '-0.04em',
            }}>
              D
            </Typography>
          )}
        </Box>

        {/* Navigation */}
        <List sx={{ flex: 1, px: (isOpen || isMobile) ? 1.5 : 1, py: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {menuItems.map((item) => {
            const active = location.pathname === item.path;
            const expanded = isOpen || isMobile;
            return (
              <ListItem key={item.text} disablePadding>
                <Tooltip title={!expanded ? item.text : ''} placement="right" arrow>
                  <ListItemButton
                    onClick={() => handleNav(item.path)}
                    sx={{
                      borderRadius: '8px',
                      px: expanded ? 1.5 : 0,
                      py: 1.25,
                      minHeight: 44,
                      backgroundColor: active ? 'rgba(255,255,255,0.06)' : 'transparent',
                      border: active ? '1px solid rgba(255,255,255,0.1)' : '1px solid transparent',
                      justifyContent: expanded ? 'flex-start' : 'center',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.04)',
                        borderColor: 'rgba(255,255,255,0.07)',
                      },
                      '& .MuiListItemIcon-root': {
                        color: active ? '#FFFFFF' : 'rgba(255,255,255,0.4)',
                        minWidth: expanded ? 36 : 'auto',
                        justifyContent: 'center',
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    {expanded && (
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          sx: {
                            color: active ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
                            fontFamily: 'Inter, sans-serif',
                            fontSize: '14px',
                            fontWeight: active ? 600 : 400,
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>

        {/* User section */}
        {user && (
          <Box
            sx={{
              px: (isOpen || isMobile) ? 2 : 1,
              py: 2,
              borderTop: '1px solid rgba(255,255,255,0.05)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              justifyContent: (isOpen || isMobile) ? 'flex-start' : 'center',
              minHeight: 72,
              flexDirection: (isOpen || isMobile) ? 'row' : 'column',
            }}
          >
            <Tooltip title={!(isOpen || isMobile) ? user.email : ''} placement="right">
              <Box sx={{ flexShrink: 0 }}>
                <UserAvatar email={user.email} size={34} />
              </Box>
            </Tooltip>

            {(isOpen || isMobile) && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ color: '#fff', fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user.email}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontFamily: 'Inter, sans-serif', fontSize: '11px', textTransform: 'capitalize' }}>
                  {user.role}
                </Typography>
              </Box>
            )}

            <Tooltip title="Log out" placement={(isOpen || isMobile) ? 'top' : 'right'}>
              <IconButton
                onClick={handleLogout}
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.3)',
                  flexShrink: 0,
                  '&:hover': { color: '#EF4444', backgroundColor: 'rgba(239,68,68,0.08)' },
                }}
              >
                <LogoutIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </>
  );
}
